// PushSettings.jsx — Manage smart push notifications (subscribe / test / unsubscribe)
import React, { useEffect, useState } from "react";
import { api } from "./lib/api";
import { subscribeToPush, unsubscribeFromPush, pushStatus, sendTestPush } from "./lib/pwa";
import { showToast } from "./lib/theme";
import { formatKSA } from "./lib/ksaTime";

export default function PushSettings() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [supported] = useState("Notification" in window && "serviceWorker" in navigator && "PushManager" in window);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");

  const refresh = async () => { setStatus(await pushStatus(api)); };
  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush(api);
      if (!r.ok) { showToast({ kind: "danger", message: r.reason }); }
      else { showToast({ kind: "success", message: "🔔 تم تفعيل الإشعارات الذكية" }); }
      setPermission(Notification.permission);
      await refresh();
    } catch (e) {
      showToast({ kind: "danger", message: e.message || "خطأ" });
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush(api);
      showToast({ kind: "info", message: "تم إيقاف الإشعارات" });
      await refresh();
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush(api);
      if (r.sent > 0) showToast({ kind: "success", message: `✅ تم إرسال "${r.title}"` });
      else showToast({ kind: "danger", message: "لم يُرسل — تأكد من تفعيل الإشعارات وVAPID" });
    } catch (e) {
      showToast({ kind: "danger", message: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  if (!supported) {
    return (
      <div className="card" style={{ marginBottom: 14, padding: 12, background: "var(--warn-soft, #fef3c7)" }} data-testid="push-not-supported">
        🔔 <b>الإشعارات الذكية</b> — متصفحك لا يدعم Web Push (Safari قديم/iOS &lt; 16.4 مثلاً).
      </div>
    );
  }

  return (
    <div className="card" data-testid="push-settings" style={{ marginBottom: 14 }}>
      <h4 style={{ margin: "0 0 8px", color: "var(--brown-deep)" }}>🔔 الإشعارات الذكية</h4>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--muted)", lineHeight: 1.7 }}>
        نرسل لك تنبيه قصير في <b>وقت ذروة نشاطك</b> فقط (مُستخرج من تعلّمك التكيّفي).
        مثلاً: "صباح الأحد محمد، خطأ حرج بانتظارك + تقريرك جاهز".
        أقصى تنبيه واحد في اليوم.
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, background: status?.subscribed ? "var(--success-soft, #ecfdf5)" : "var(--warn-soft, #fef3c7)", borderRadius: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 22 }}>{status?.subscribed ? "🔔✅" : "🔕"}</span>
        <div style={{ flex: 1 }}>
          <b style={{ color: status?.subscribed ? "var(--success)" : "var(--warn)" }}>
            {status?.subscribed ? "مُفعّلة" : "غير مُفعّلة"}
          </b>
          {status?.last_sent && (
            <div style={{ fontSize: 11, color: "var(--muted)" }}>
              آخر إشعار: {formatKSA(new Date(status.last_sent))} · "{status.last_title}"
            </div>
          )}
          {permission === "denied" && (
            <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 2 }}>
              ⚠ المتصفح مانع الإشعارات. فعّلها من إعدادات الموقع.
            </div>
          )}
        </div>
        {!status?.subscribed ? (
          <button type="button" className="btn btn-primary" onClick={enable} disabled={busy || permission === "denied"} data-testid="push-enable">
            {busy ? "..." : "فعّل الإشعارات"}
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-soft" onClick={sendTest} disabled={busy} data-testid="push-test">
              🧪 اختبار
            </button>
            <button type="button" className="btn" onClick={disable} disabled={busy} data-testid="push-disable"
                    style={{ background: "var(--danger)", color: "#fff" }}>إيقاف</button>
          </>
        )}
      </div>
      <p style={{ fontSize: 10.5, color: "var(--muted)", margin: 0 }}>
        💡 الإشعار يُولّد من Claude بناءً على دورك ونبذتك وأحداث اليوم. لا يحتوي بيانات حساسة.
      </p>

      {status?.subscribed && <PushPrefsInline />}
    </div>
  );
}

// ─── Push Preferences (v119.184) ──────────────────────────────────────
// Per-user filter of which notification types to receive as web push,
// plus optional quiet-hours window (KSA time).
const TYPE_META = [
  { key: "alert",   label: "🚨 التنبيهات الحرجة", desc: "هبوط مبيعات، أخطاء POS، شكاوى عاجلة" },
  { key: "task",    label: "✅ المهام",              desc: "مهام جديدة موكلة إليك أو مواعيد استحقاق" },
  { key: "meeting", label: "📅 الاجتماعات",         desc: "دعوات، تذكيرات، ملخصات AI" },
  { key: "message", label: "💬 الرسائل",            desc: "واتساب من العملاء، مراسلات داخلية" },
  { key: "system",  label: "⚙️ إشعارات النظام",     desc: "تحديثات، نسخ احتياطي، تقارير دورية (مزعج نسبياً)" },
];

function PushPrefsInline() {
  const [prefs, setPrefs] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);

  React.useEffect(() => {
    api.get("/push/prefs").then(r => setPrefs(r.data)).catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.put("/push/prefs", next);
      setPrefs(r.data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (_) { /* silent */ }
    finally { setSaving(false); }
  };

  const toggleType = (k, v) => {
    if (!prefs) return;
    const nextTypes = { ...prefs.types, [k]: v };
    save({ ...prefs, types: nextTypes });
  };

  const saveQuiet = (start, end) => {
    if (!prefs) return;
    save({ ...prefs, quiet_hours_start: start || null, quiet_hours_end: end || null });
  };

  if (!prefs) return null;

  return (
    <div data-testid="push-prefs" style={{
      marginTop: 14, padding: 14, borderRadius: 10,
      background: "linear-gradient(135deg, var(--beige-soft), transparent)",
      border: "1px solid var(--border)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: "var(--brown-deep)" }}>
            🎛️ تفضيلات الإشعارات
          </div>
          <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>
            اختر الأنواع التي تريد استقبالها فوراً على الجوال
          </div>
        </div>
        {savedFlash && (
          <span style={{ fontSize: 11, color: "var(--success)", fontWeight: 700 }} data-testid="push-prefs-saved">
            ✓ حُفظ
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {TYPE_META.map(({ key, label, desc }) => {
          const on = !!prefs.types[key];
          return (
            <label key={key} data-testid={`push-pref-${key}`} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 10px", background: "#fff",
              borderRadius: 8, border: `1px solid ${on ? "var(--gold-soft)" : "var(--border)"}`,
              cursor: "pointer", transition: "border-color .15s ease",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "var(--brown-deep)" }}>{label}</div>
                <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 1 }}>{desc}</div>
              </div>
              <input
                type="checkbox"
                checked={on}
                disabled={saving}
                onChange={(e) => toggleType(key, e.target.checked)}
                data-testid={`push-pref-toggle-${key}`}
                style={{ width: 18, height: 18, cursor: "pointer", accentColor: "var(--gold)" }}
              />
            </label>
          );
        })}
      </div>

      {/* Quiet hours */}
      <div style={{
        marginTop: 12, padding: 10, background: "#fff",
        borderRadius: 8, border: "1px solid var(--border)",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 6 }}>
          🌙 ساعات الهدوء (توقيت السعودية)
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ fontSize: 11, color: "var(--muted)" }}>من:</label>
          <input
            type="time" value={prefs.quiet_hours_start || ""}
            onChange={(e) => saveQuiet(e.target.value, prefs.quiet_hours_end)}
            data-testid="push-pref-quiet-start"
            style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12 }}
          />
          <label style={{ fontSize: 11, color: "var(--muted)" }}>إلى:</label>
          <input
            type="time" value={prefs.quiet_hours_end || ""}
            onChange={(e) => saveQuiet(prefs.quiet_hours_start, e.target.value)}
            data-testid="push-pref-quiet-end"
            style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid var(--border)", fontSize: 12 }}
          />
          {(prefs.quiet_hours_start || prefs.quiet_hours_end) && (
            <button type="button" onClick={() => saveQuiet("", "")}
                    data-testid="push-pref-quiet-clear"
                    style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: 11 }}>
              مسح ✕
            </button>
          )}
        </div>
        <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 5 }}>
          خلال هذه الفترة لن تصلك أي إشعارات push (تظل داخل التطبيق).
        </div>
      </div>
    </div>
  );
}
